import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function Users({ token, apiBaseUrl }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const REFRESH_INTERVAL = 30000;

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Generate license modal state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateEmail, setGenerateEmail] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [statusFilter]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchUsers(true);
    }, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [autoRefresh, statusFilter, searchTerm]);

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    const now = new Date();
    const diffSeconds = Math.floor((now - lastUpdated) / 1000);
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    return lastUpdated.toLocaleTimeString();
  };

  const fetchUsers = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${apiBaseUrl}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data.users || []);
      setLastUpdated(new Date());
      setError('');
      setSelectedUsers(new Set());
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(false);
  };

  const handleManualRefresh = () => {
    fetchUsers(true);
  };

  const handleRevokeUser = async (email) => {
    if (!window.confirm(`Are you sure you want to revoke access for ${email}?`)) {
      return;
    }
    try {
      await axios.post(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}/revoke`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers(true);
      alert('User access revoked successfully');
    } catch (err) {
      alert('Failed to revoke user access');
      console.error(err);
    }
  };

  const handleReactivateUser = async (email) => {
    if (!window.confirm(`Are you sure you want to reactivate access for ${email}?`)) {
      return;
    }
    try {
      await axios.post(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers(true);
      alert('User access reactivated successfully');
    } catch (err) {
      alert('Failed to reactivate user access');
      console.error(err);
    }
  };

  const handleSyncStatus = async (email) => {
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}/refresh-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.status === 'active') {
        alert(response.data.message);
        fetchUsers(true);
      } else {
        alert(response.data.message || 'Status unchanged');
      }
    } catch (err) {
      alert('Failed to sync status');
      console.error(err);
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(new Set(users.map(u => u.email || u.beta_user_id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleBulkRevoke = async () => {
    const selectedEmails = users
      .filter(u => selectedUsers.has(u.email || u.beta_user_id) && u.email && (u.status === 'active' || u.status === 'expired'))
      .map(u => u.email);

    if (selectedEmails.length === 0) {
      alert('No active users with email selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to revoke access for ${selectedEmails.length} user(s)?`)) {
      return;
    }

    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const email of selectedEmails) {
      try {
        await axios.post(`${apiBaseUrl}/admin/users/${encodeURIComponent(email)}/revoke`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to revoke ${email}:`, err);
      }
    }

    setBulkActionLoading(false);
    alert(`Revoked ${successCount} user(s). ${failCount > 0 ? `Failed: ${failCount}` : ''}`);
    fetchUsers(true);
  };

  // Export functions
  const exportToCSV = (usersToExport, filename) => {
    const headers = ['Email', 'User ID', 'License Type', 'Status', 'Registration Date', 'License Key'];
    const rows = usersToExport.map(u => [
      u.email || '',
      u.beta_user_id || u.user_id || '',
      u.license_type || 'lifetime',
      u.status === 'expired' ? 'active' : (u.status || 'inactive'),
      u.registration_date || '',
      u.license_key || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportAll = () => {
    exportToCSV(users, `terraback-users-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleExportSelected = () => {
    const selectedUsersList = users.filter(u => selectedUsers.has(u.email || u.beta_user_id));
    if (selectedUsersList.length === 0) {
      alert('No users selected');
      return;
    }
    exportToCSV(selectedUsersList, `terraback-users-selected-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Generate license handler
  const handleGenerateLicense = async (e) => {
    e.preventDefault();
    if (!generateEmail.trim()) {
      setGenerateError('Email is required');
      return;
    }

    setGenerateLoading(true);
    setGenerateError('');

    try {
      await axios.post(`${apiBaseUrl}/admin/generate-license`, {
        email: generateEmail.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('License generated successfully');
      setShowGenerateModal(false);
      setGenerateEmail('');
      fetchUsers(true);
    } catch (err) {
      setGenerateError(err.response?.data?.message || 'Failed to generate license');
    } finally {
      setGenerateLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.inactive}`}>
        {status || 'inactive'}
      </span>
    );
  };

  const getLicenseTypeBadge = (type) => {
    const colors = {
      lifetime: 'bg-emerald-100 text-emerald-800',
      professional: 'bg-indigo-100 text-indigo-800',
      enterprise: 'bg-pink-100 text-pink-800'
    };
    const displayType = (type === 'beta' || type === 'trial' || type === 'professional') ? 'lifetime' : type;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[displayType] || colors.lifetime}`}>
        {displayType || 'lifetime'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const allSelected = users.length > 0 && selectedUsers.size === users.length;
  const someSelected = selectedUsers.size > 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">License Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage lifetime licenses and user access
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex items-center gap-3">
          <button
            onClick={() => setShowGenerateModal(true)}
            className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
          >
            + Generate License
          </button>
          <button
            onClick={handleExportAll}
            className="inline-flex items-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            Export CSV
          </button>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Auto-refresh</label>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${autoRefresh ? 'bg-blue-600' : 'bg-gray-200'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${autoRefresh ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
          >
            {refreshing && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            Refresh
          </button>
          {lastUpdated && (
            <span className="text-xs text-gray-400">
              Updated {formatLastUpdated()}
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            placeholder="Search by email or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
          />
          <button
            type="submit"
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Search
          </button>
        </form>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {/* Bulk Actions Bar */}
      {someSelected && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm text-blue-800">
            {selectedUsers.size} user(s) selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleExportSelected}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            >
              Export Selected
            </button>
            <button
              onClick={handleBulkRevoke}
              disabled={bulkActionLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {bulkActionLoading ? 'Processing...' : 'Revoke Selected'}
            </button>
            <button
              onClick={() => setSelectedUsers(new Set())}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-gray-600 hover:text-gray-800"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 text-red-600 text-center">{error}</div>
      )}

      {/* Users Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                    User
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    License
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Purchased
                  </th>
                  <th className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => {
                  const userId = user.email || user.beta_user_id;
                  return (
                    <tr key={userId} className={`hover:bg-gray-50 ${selectedUsers.has(userId) ? 'bg-blue-50' : ''}`}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-0">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(userId)}
                          onChange={() => handleSelectUser(userId)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                        <div>
                          <div className="font-medium text-gray-900">{user.email || 'No email'}</div>
                          <div className="text-gray-500 text-xs">{user.beta_user_id || user.user_id}</div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {getLicenseTypeBadge(user.license_type)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {getStatusBadge(user.status === 'expired' ? 'active' : user.status)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <div>{formatDate(user.registration_date)}</div>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                        {user.email ? (
                          <Link
                            to={`/users/${encodeURIComponent(user.email)}`}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            Details
                          </Link>
                        ) : (
                          <span className="text-gray-400 mr-3">No details</span>
                        )}
                        {(user.status === 'active' || user.status === 'expired') && user.email && (
                          <button
                            onClick={() => handleRevokeUser(user.email)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Revoke
                          </button>
                        )}
                        {user.status === 'pending' && user.email && (
                          <button
                            onClick={() => handleSyncStatus(user.email)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Sync
                          </button>
                        )}
                        {user.status === 'revoked' && user.email && (
                          <button
                            onClick={() => handleReactivateUser(user.email)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {users.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-500">
                No users found matching your criteria
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Summary */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{users.length}</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Active</dt>
            <dd className="mt-1 text-3xl font-semibold text-green-600">
              {users.filter(u => u.status === 'active' || u.status === 'expired').length}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
            <dd className="mt-1 text-3xl font-semibold text-yellow-600">
              {users.filter(u => u.status === 'pending').length}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Revoked</dt>
            <dd className="mt-1 text-3xl font-semibold text-red-600">
              {users.filter(u => u.status === 'revoked').length}
            </dd>
          </div>
        </div>
      </div>

      {/* Generate License Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Generate New License</h3>
            </div>
            <form onSubmit={handleGenerateLicense}>
              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 mb-4">
                  Generate a new lifetime license for a user. This will create a license key and send it to the specified email.
                </p>
                <label className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  value={generateEmail}
                  onChange={(e) => setGenerateEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="user@example.com"
                  required
                />
                {generateError && (
                  <p className="mt-2 text-sm text-red-600">{generateError}</p>
                )}
              </div>
              <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowGenerateModal(false);
                    setGenerateEmail('');
                    setGenerateError('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={generateLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {generateLoading ? 'Generating...' : 'Generate License'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
